import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { siteSettings as mockSettings } from '../data/mock';
import { contentAPI } from '../services/api';
import { ContactForm } from '../components/contact/ContactForm';
import { OfficeCard, OfficeMapCard } from '../components/contact/OfficeCards';

const OFFICE_ORDER = [
  { key: 'head', color: 'emerald', title: 'Head Office Location' },
  { key: 'haryana', color: 'blue', title: 'Haryana Office Location' },
  { key: 'maharashtra', color: 'orange', title: 'Maharashtra Office Location' },
  { key: 'rajasthan', color: 'yellow', title: 'Rajasthan Office Location' },
];

const Contact = () => {
  const [siteSettings, setSiteSettings] = useState(mockSettings);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('site_settings').then(res => {
      if (res.data && Object.keys(res.data).length) {
        // Use database data BUT always use hardcoded offices from mockSettings
        setSiteSettings({
          ...res.data,
          offices: mockSettings.offices  // ALWAYS use mock offices, ignore database
        });
      }
    }).catch(() => {});
  }, []);

  const offices = OFFICE_ORDER.map(o => ({ ...o, data: siteSettings.offices?.[o.key] })).filter(o => o.data);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Get In Touch</h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            Ready to start your renewable energy journey? Contact our experts for tailored solutions
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Reach out to us for expert consultation on renewable energy projects, regulatory approvals, and legal advisory services.
              </p>

              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-1">Email</h3>
                  <a href={`mailto:${siteSettings.email}`} className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="contact-email-link">
                    {siteSettings.email}
                  </a>
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-4">Our Offices</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {offices.map(o => <OfficeCard key={o.key} office={o.data} color={o.color} testId={`office-card-${o.key}`} />)}
              </div>
            </div>

            <div className="lg:col-span-3">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Maps Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Visit Our Offices</h2>
            <p className="text-lg text-gray-600">We're here to serve you across India</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {offices.map(o => <OfficeMapCard key={o.key} office={o.data} color={o.color} title={o.title} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
